import { Address, extractAddress } from "./address";
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../../hooks/use-theme";
import { ActivityIndicator, Pressable, StyleSheet, TextInput, View } from "react-native";
import Constants from "expo-constants";
import { GooglePlacesAutocomplete } from "./GooglePlacesAutocomplete";
import {
    GooglePlaceData,
    GooglePlacesAutocompleteRef,
} from "react-native-google-places-autocomplete";
import ListItem from "../../ui_atoms/ListItem";
import Modal from "../Modal";
import Icon from "../Icon";
import TextField from "../TextField";
import { useI18n } from "../../hooks/i18n-hooks";
import Gap from "../../ui_atoms/Gap";

export type AddressChooserProps = {
    language?: string;
    onSelect?: (address: Address) => void;
    onDismiss: () => void;
    visible: boolean;
    inputPlaceholder?: string;
    inputLabel?: string;
    allowedCountries?: string[]; // maximum 5 countries :https://developers.google.com/maps/documentation/places/web-service/autocomplete?_gl=1*1pabl58*_up*MQ..*_ga*NzcxOTA0ODk1LjE3Mjc4NjE1Njc.*_ga_NRWSTWS78N*MTcyNzg2MTU2Ny4xLjAuMTcyNzg2MTY0My4wLjAuMA..
    initialValue?: string;
};

type HeaderFieldProps = {
    onDismiss: () => void;
    isLocationLoading: boolean;
    onLocation: () => void;
};

// eslint-disable-next-line react/display-name
const HeaderField: React.FC<HeaderFieldProps> = forwardRef<TextInput, HeaderFieldProps>(
    ({ onDismiss, isLocationLoading, onLocation, ...props }, InputRef) => {
        const style = useHeaderStyle();
        const i18n = useI18n();

        return (
            <View style={style.wrapper}>
                <View style={style.container}>
                    <Pressable onPress={onDismiss} style={style.backButton}>
                        <Icon name="close" size={28} />
                    </Pressable>
                    <TextField
                        {...props}
                        ref={InputRef}
                        clearButtonMode="never"
                        style={style.textField}
                    />
                </View>
                <Gap gap={0.75} />
                <ListItem
                    {...(isLocationLoading
                        ? {
                              iconComponent: <ActivityIndicator size={24} />,
                              disabled: true,
                          }
                        : { icon: "map-marker" })}
                    rightActionMode="empty"
                    description={i18n.t("your_location")}
                    onPress={onLocation}
                />
            </View>
        );
    },
);

const useHeaderStyle = () => {
    const { gridSize, marginSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                wrapper: {
                    flexDirection: "column",
                    flexShrink: 1,
                },
                container: {
                    marginHorizontal: marginSize,
                    flexDirection: "row",
                    alignItems: "center",
                },
                backButton: {
                    flexGrow: 0,
                    flexShrink: 1,
                    width: 28,
                },
                textField: {
                    backgroundColor: "white",
                    flexShrink: 1,
                    marginLeft: gridSize / 2,
                },
            }),
        [gridSize, marginSize],
    );
};

if (process.env.NODE_ENV !== "test") {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    navigator.geolocation = require("@react-native-community/geolocation");
}

export const AddressChooser: React.FC<AddressChooserProps> = ({
    language = "en",
    onSelect,
    onDismiss,
    visible,
    inputLabel,
    inputPlaceholder,
    allowedCountries,
    initialValue = "",
}) => {
    const { gridSize, marginSize } = useTheme();
    const ref = useRef<GooglePlacesAutocompleteRef>(null);

    const [isLocationLoading, setIsLocationLoading] = useState(false);

    const onLocation = useCallback(() => {
        if (ref.current?.getCurrentLocation) {
            setIsLocationLoading(true);
            ref.current.getCurrentLocation();
        }
    }, []);

    const renderRow = useCallback((data: GooglePlaceData) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const gpsLocation = data.vicinity;

        return (
            <ListItem
                style={{ alignSelf: "flex-start", flexShrink: 1, width: "100%" }}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                icon={data.isCurrentLocation || gpsLocation ? "map-marker" : undefined}
                title={data.structured_formatting?.main_text}
                description={data.description || gpsLocation}
            />
        );
    }, []);

    useEffect(() => {
        if (visible && initialValue) {
            ref.current?.setAddressText(initialValue);
        }
    }, [visible, initialValue]);

    return (
        <Modal
            visible={visible}
            animationType="slide"
            statusBarTranslucent
            // workaround to fix React native bug @see https://github.com/react-native-modal/react-native-modal/issues/516
            onShow={() => setTimeout(() => ref.current?.focus(), 100)}
        >
            <GooglePlacesAutocomplete
                ref={ref}
                GooglePlacesSearchQuery={{
                    rankby: "distance",
                }}
                enableHighAccuracyLocation={false}
                placeholder={inputPlaceholder || ""}
                onLocationEnd={() => {
                    setIsLocationLoading(false);
                }}
                onPress={(_, details = null) => {
                    if (details) {
                        const address = extractAddress(details);
                        if (onSelect && address) {
                            onSelect(address);
                        } else {
                            console.warn("Unable to extract address from details", details);
                            ref.current?.clear();
                            ref.current?.focus();
                        }
                    }
                }}
                fetchDetails={true}
                onFail={(x) => {
                    console.warn("Google map address lookup failed", x);
                }}
                query={{
                    key: Constants.expoConfig?.extra?.googleApiKey,
                    language,
                    components: allowedCountries
                        ?.slice(0, 5)
                        .map((c) => `country:${c}`)
                        .join("|"),
                    type: "address",
                }}
                styles={{
                    container: {
                        marginHorizontal: 0,
                    },

                    row: {
                        marginVertical: gridSize / 4,
                        paddingVertical: 0,
                        paddingHorizontal: 0,
                        flexDirection: "row",
                        backgroundColor: "transparent",
                    },
                    listView: {
                        backgroundColor: "transparent",
                        alignSelf: "stretch",
                    },
                    separator: {
                        height: 1,
                        backgroundColor: "transparent",
                    },
                }}
                keepResultsAfterBlur
                renderRow={renderRow}
                GooglePlacesDetailsQuery={{
                    fields: "",
                }}
                textInputProps={{
                    InputComp: HeaderField,
                    label: inputLabel,
                    isLocationLoading,
                    onLocation,
                    clearButtonMode: "while-editing",
                    style: {
                        backgroundColor: "white",
                        marginHorizontal: marginSize,
                        paddingRight: 10,
                    },
                    onDismiss,
                }}
                isRowScrollable={false}
                enablePoweredByContainer={false}
            />
        </Modal>
    );
};
